/**
 * Scheduler Jobs Status Page
 *
 * Ops board showing the last run time, items processed, and recent errors
 * for every background scheduler job.
 *
 * @fileoverview Admin scheduler jobs status page
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Badge,
  Skeleton,
  useColorModeValue,
  Icon,
  Divider,
  Tooltip,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Code,
  Collapse,
  useDisclosure,
  SimpleGrid,
} from '@chakra-ui/react';
import {
  MdRefresh,
  MdCheckCircle,
  MdError,
  MdSchedule,
  MdExpandMore,
  MdExpandLess,
  MdPauseCircle,
  MdPlayCircle,
} from 'react-icons/md';
import { adminApi } from '../../services/adminApi';
import { SchedulerJobStatus, JobsStatusResponse } from '../../types/admin';
import { toUserMessage } from '../../lib/apiError';

// ─── helpers ─────────────────────────────────────────────────────────────────

function formatRelativeTime(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(diff / 86_400_000);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'just now';
}

function formatAbsoluteTime(isoDate: string): string {
  return new Date(isoDate).toLocaleString();
}

// ─── MetadataTable ─────────────────────────────────────────────────────────

interface MetadataTableProps {
  metadata: Record<string, unknown>;
}

const METADATA_LABELS: Record<string, string> = {
  tenantsProcessed: 'Tenants processed',
  successes: 'Succeeded',
  failures: 'Failed',
  tokensVerified: 'Tokens verified',
  healthyItems: 'Healthy',
  unknownTransitions: '→ UNKNOWN',
  unknownStillFailing: 'Still failing',
  timestamp: 'Run at',
  error: 'Error',
};

const MetadataTable: React.FC<MetadataTableProps> = ({ metadata }) => {
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  const entries = Object.entries(metadata).filter(
    ([key]) => key !== 'timestamp'
  );

  if (entries.length === 0) return null;

  return (
    <SimpleGrid columns={2} spacing={2} mt={2}>
      {entries.map(([key, val]) => (
        <HStack key={key} spacing={2} align="start">
          <Text fontSize="xs" color={mutedColor} whiteSpace="nowrap">
            {METADATA_LABELS[key] ?? key}:
          </Text>
          <Text fontSize="xs" fontWeight="semibold">
            {String(val)}
          </Text>
        </HStack>
      ))}
    </SimpleGrid>
  );
};

// ─── ErrorList ────────────────────────────────────────────────────────────────

interface ErrorListProps {
  errors: SchedulerJobStatus['recentErrors'];
}

const ErrorList: React.FC<ErrorListProps> = ({ errors }) => {
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  // Hoisted out of the .map() below: it's the same value for every item, so
  // calling it once per item bought nothing — and since `errors` is
  // polling-refreshed data whose length changes over this component's
  // lifetime (it stays mounted inside a <Collapse>, not remounted), calling
  // a hook once per array item made the hook count for this instance vary
  // between renders (React: "has detected a change in the order of Hooks").
  const errorBg = useColorModeValue('red.50', 'red.900');

  return (
    <VStack align="stretch" spacing={2}>
      {errors.map((err, i) => (
        <Box key={i} p={2} bg={errorBg} borderRadius="md">
          <HStack justify="space-between" mb={1}>
            <Text fontSize="xs" color={mutedColor}>
              {formatAbsoluteTime(err.timestamp)}
            </Text>
            {err.context && (
              <Code fontSize="xs" colorScheme="red">
                {err.context}
              </Code>
            )}
          </HStack>
          <Text fontSize="xs" fontFamily="mono" noOfLines={3}>
            {err.message}
          </Text>
        </Box>
      ))}
    </VStack>
  );
};

// ─── JobCard ─────────────────────────────────────────────────────────────────

interface JobCardProps {
  job: SchedulerJobStatus;
}

const JobCard: React.FC<JobCardProps> = ({ job }) => {
  const { isOpen: errorsOpen, onToggle: toggleErrors } = useDisclosure();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const errorBorder = useColorModeValue('red.200', 'red.700');

  const hasErrors = job.recentErrors.length > 0;
  const hasActivity = job.lastRun.lastActivityAt !== null;
  const isDisabled = !job.isEnabled;

  const statusColor = isDisabled
    ? 'gray'
    : job.lastRun.status === 'success'
    ? 'green'
    : job.lastRun.status === 'error'
    ? 'red'
    : 'gray';

  const StatusIcon = isDisabled
    ? MdPauseCircle
    : job.lastRun.status === 'success'
    ? MdCheckCircle
    : job.lastRun.status === 'error'
    ? MdError
    : MdSchedule;

  return (
    <Box
      bg={bgColor}
      border="1px"
      borderColor={hasErrors ? errorBorder : borderColor}
      borderRadius="lg"
      p={4}
      shadow="sm"
    >
      {/* Header */}
      <HStack justify="space-between" mb={2}>
        <HStack spacing={2}>
          <Icon
            as={StatusIcon}
            color={`${statusColor}.400`}
            boxSize={5}
          />
          <Text fontWeight="semibold" fontSize="sm">
            {job.displayName}
          </Text>
        </HStack>
        <HStack spacing={1}>
          {isDisabled && (
            <Badge colorScheme="gray" variant="subtle" fontSize="xs">
              Disabled
            </Badge>
          )}
          {hasErrors && (
            <Badge colorScheme="red" variant="subtle" fontSize="xs">
              {job.recentErrors.length} error{job.recentErrors.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </HStack>
      </HStack>

      {/* Description */}
      <Text fontSize="xs" color={mutedColor} mb={3} lineHeight="tall">
        {job.description}
      </Text>

      <Divider mb={3} />

      {/* Schedule */}
      <HStack spacing={2} mb={2}>
        <Icon as={MdSchedule} color={mutedColor} boxSize={3.5} />
        <Text fontSize="xs" color={mutedColor}>
          {job.schedule}
        </Text>
        {job.scheduleExpression && (
          <Code fontSize="xs" colorScheme="gray">
            {job.scheduleExpression}
          </Code>
        )}
      </HStack>

      {/* Last activity */}
      <HStack spacing={2} mb={hasActivity && job.lastRun.metadata ? 1 : 0}>
        <Icon
          as={isDisabled ? MdPauseCircle : job.lastRun.status === 'success' ? MdPlayCircle : MdSchedule}
          color={mutedColor}
          boxSize={3.5}
        />
        {hasActivity ? (
          <Tooltip label={formatAbsoluteTime(job.lastRun.lastActivityAt!)}>
            <Text fontSize="xs" color={mutedColor}>
              Last activity:{' '}
              <Text as="span" fontWeight="semibold" color={`${statusColor}.500`}>
                {formatRelativeTime(job.lastRun.lastActivityAt!)}
              </Text>
            </Text>
          </Tooltip>
        ) : (
          <Text fontSize="xs" color={mutedColor} fontStyle="italic">
            {isDisabled ? 'Job is disabled' : 'No activity recorded yet'}
          </Text>
        )}
      </HStack>

      {/* Last run metadata */}
      {job.lastRun.metadata && Object.keys(job.lastRun.metadata).length > 0 && (
        <MetadataTable metadata={job.lastRun.metadata} />
      )}

      {/* Recent errors toggle */}
      {hasErrors && (
        <>
          <Divider mt={3} mb={2} />
          <Button
            size="xs"
            variant="ghost"
            colorScheme="red"
            leftIcon={<Icon as={errorsOpen ? MdExpandLess : MdExpandMore} />}
            onClick={toggleErrors}
            w="full"
            justifyContent="flex-start"
          >
            {errorsOpen ? 'Hide' : 'Show'} recent errors ({job.recentErrors.length})
          </Button>
          <Collapse in={errorsOpen} animateOpacity>
            <Box mt={2}>
              <ErrorList errors={job.recentErrors} />
            </Box>
          </Collapse>
        </>
      )}
    </Box>
  );
};

// ─── main page ────────────────────────────────────────────────────────────────

export const JobsStatusPage: React.FC = () => {
  const [data, setData] = useState<JobsStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  // Hoisted above the summary-stats JSX below: those two calls used to be
  // inline inside a .map() over a fixed 4-item array, but the whole
  // SimpleGrid is itself gated behind `{!loading && data && (...)}` — so
  // the hook count for this component was 8 while that block rendered and
  // 0 while it didn't, changing between renders as `loading`/`data`
  // resolve (React: "has detected a change in the order of Hooks").
  const statCardBg = useColorModeValue('white', 'gray.800');
  const statCardBorderColor = useColorModeValue('gray.200', 'gray.700');

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminApi.jobs.getStatus();
      setData(result);
    } catch (err) {
      setError(toUserMessage(err, "We couldn't load the job status. Please try again."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const enabledJobs = data?.jobs.filter((j) => j.isEnabled) ?? [];
  const disabledJobs = data?.jobs.filter((j) => !j.isEnabled) ?? [];
  const jobsWithErrors = data?.jobs.filter((j) => j.recentErrors.length > 0) ?? [];

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <HStack justify="space-between" wrap="wrap" gap={4}>
        <Box>
          <HStack spacing={2} mb={1}>
            <Icon as={MdSchedule} color="blue.400" boxSize={6} />
            <Heading size="lg">Scheduler Status</Heading>
          </HStack>
          <Text color={mutedColor} fontSize="sm">
            Last run time, items processed, and recent errors for each background job
          </Text>
        </Box>
        <Button
          leftIcon={<Icon as={MdRefresh} />}
          variant="outline"
          size="sm"
          onClick={fetch}
          isLoading={loading}
        >
          Refresh
        </Button>
      </HStack>

      {/* Error */}
      {error && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertTitle>Failed to load</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Summary stats */}
      {!loading && data && (
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          {[
            { label: 'Active jobs', value: enabledJobs.length, color: 'blue.500' },
            { label: 'Disabled jobs', value: disabledJobs.length, color: 'gray.400' },
            {
              label: 'Jobs with errors',
              value: jobsWithErrors.length,
              color: jobsWithErrors.length > 0 ? 'red.500' : 'green.500',
            },
            {
              label: 'Total recent errors',
              value: data.jobs.reduce((sum, j) => sum + j.recentErrors.length, 0),
              color: data.jobs.some((j) => j.recentErrors.length > 0) ? 'red.500' : 'green.500',
            },
          ].map(({ label, value, color }) => (
            <Box
              key={label}
              bg={statCardBg}
              border="1px"
              borderColor={statCardBorderColor}
              borderRadius="lg"
              p={4}
              textAlign="center"
            >
              <Text fontSize="2xl" fontWeight="bold" color={color}>
                {value}
              </Text>
              <Text fontSize="xs" color={mutedColor}>
                {label}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      )}

      {/* Skeletons */}
      {loading && (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} h="180px" borderRadius="lg" />
          ))}
        </SimpleGrid>
      )}

      {/* Active jobs */}
      {!loading && data && enabledJobs.length > 0 && (
        <>
          <HStack justify="space-between">
            <Text fontSize="sm" fontWeight="semibold" color={mutedColor}>
              Active Jobs ({enabledJobs.length})
            </Text>
            {data.queriedAt && (
              <Text fontSize="xs" color={mutedColor}>
                Queried: {new Date(data.queriedAt).toLocaleTimeString()}
              </Text>
            )}
          </HStack>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {enabledJobs.map((job) => (
              <JobCard key={job.id} job={job} />
            ))}
          </SimpleGrid>
        </>
      )}

      {/* Disabled jobs */}
      {!loading && data && disabledJobs.length > 0 && (
        <>
          <Text fontSize="sm" fontWeight="semibold" color={mutedColor}>
            Disabled Jobs ({disabledJobs.length})
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {disabledJobs.map((job) => (
              <JobCard key={job.id} job={job} />
            ))}
          </SimpleGrid>
        </>
      )}
    </VStack>
  );
};

export default JobsStatusPage;
